import { useTranslation } from '../../shared/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Row, Col, Button, Typography, Space, Tag, List, Divider,
    message, Modal, Spin, Alert, Segmented, Empty
} from 'antd';
import {
    CheckOutlined, CloseOutlined, RocketOutlined, CrownOutlined,
    ThunderboltOutlined, StarOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { billingService } from '../services/billingService';
import { cartService } from '../services/cartService';
import apiClient from '../../shared/services/apiClient';
import { userPath } from '../../shared/config/paths';
import { formatMoney } from '../../shared/utils/money';
import PageHeader from '../../shared/components/PageHeader';
import AddonPanel from '../../shared/components/AddonPanel';
import { Tabs } from 'antd';

const { Title, Text } = Typography;

/**
 * 产品套餐页（DNS / CDN 共用）
 *
 * 2026-10-07：套餐、价格、配额全部来自 /products（products.features = 后端执行配额的唯一事实源），
 * 不再前端硬编码。套餐总是买在资源上：必须带 resourceId（zone_id / domain_id），否则回到资源列表。
 * 选购 / 升级 / 降级都加入购物车统一结算。
 */

const CYCLES = [
    { label: '月付', value: 'monthly', months: 1 },
    { label: '季付', value: 'quarterly', months: 3 },
    { label: '半年付', value: 'semi_annually', months: 6 },
    { label: '年付', value: 'annually', months: 12 },
];
const CYCLE_UNIT = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年', onetime: '一次性' };
const TIER_ICONS = [<RocketOutlined />, <ThunderboltOutlined />, <StarOutlined />, <CrownOutlined />];
const TIER_COLORS = ['#8590a6', '#2357ff', '#10b981', '#f5a524'];

// 展示值 = 后端执行值:featureDef 带 enforcedKey 时优先取 resource_features[enforcedKey]
// (后端按 resource_features 执行配额,features 只是营销展示,两者可能不一致)。
const effectiveFeatures = (product, defs) => {
    const base = product.features && typeof product.features === 'object' ? { ...product.features } : {};
    const rf = product.resource_features && typeof product.resource_features === 'object' ? product.resource_features : {};
    (defs || []).forEach((d) => {
        if (d.enforcedKey && rf[d.enforcedKey] !== undefined && rf[d.enforcedKey] !== '' && !Number.isNaN(Number(rf[d.enforcedKey]))) {
            base[d.key] = Number(rf[d.enforcedKey]);
        }
    });
    return base;
};

const priceMapOf = (product) => {
    const map = {};
    (product.pricing || []).forEach((p) => {
        if (p.enabled === false || p.enabled === 0) return;
        map[p.billing_cycle] = { price: parseFloat(p.price), currency: p.currency };
    });
    return map;
};

/** 资源包页签：当前资源所在套餐可买的资源包与本周期用量。 */
const AddonsTab = ({ subscriptionId, free }) => {
    const { t } = useTranslation();
    if (!subscriptionId) return <Empty description={t('No plan yet')} />;
    if (free) return <Alert type="info" showIcon message={t('Free plans cannot buy add-ons; upgrade to a paid plan first')} />;
    return <AddonPanel key={subscriptionId} subscriptionId={subscriptionId} />;
};

const ProductPlans = ({
    productType,          // 'dns' | 'cdn'
    resourceParam,        // 'zone_id' | 'domain_id'
    resourceLabel,        // '区域' | '域名'
    resourceListPath,     // '/dns/zones'
    title,
    subtitle,
    featureDefs,          // [{ key, label, unit?, format?(v) }]
    notes,                // ReactNode：功能说明
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const resourceId = params.get(resourceParam) ? parseInt(params.get(resourceParam), 10) : null;

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [products, setProducts] = useState([]);
    const [currentSub, setCurrentSub] = useState(null);           // 指定资源时的订阅
    const [currentSubId, setCurrentSubId] = useState(null);
    const [upgrades, setUpgrades] = useState([]);
    const [upInfo, setUpInfo] = useState({});                     // { free, expired, pending_invoice_id }
    const [busy, setBusy] = useState(false);
    const [cycle, setCycle] = useState('monthly');
    const [tab, setTab] = useState(params.get('tab') || 'plans');

    useEffect(() => {
        let alive = true;
        if (!resourceId) return undefined;
        (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const res = await apiClient.get('/products', { params: { product_type: productType, status: 'active' } });
                const list = (res.data?.success && Array.isArray(res.data.data) ? res.data.data : [])
                    .filter((p) => !p.is_addon)
                    .sort((a, b) => (a.sort_order ?? a.display_order ?? 0) - (b.sort_order ?? b.display_order ?? 0) || a.id - b.id);
                if (!alive) return;
                setProducts(list);

                {
                    const ov = await billingService.getSubscriptionOverview(productType, resourceId).catch(() => null);
                    if (alive && ov?.success && ov.data?.has_subscription) {
                        setCurrentSub(ov.data);
                        setCurrentSubId(ov.data.subscription_id || null);
                    }
                    const up = await billingService.getAvailableUpgrades(productType, resourceId).catch(() => null);
                    if (alive && up?.success) {
                        setUpgrades(up.data?.available_upgrades || []);
                        setUpInfo({
                            free: !!up.data?.free,
                            expired: !!up.data?.expired,
                            pending_invoice_id: up.data?.pending_invoice_id || null,
                            downgrades: up.data?.downgrades || [],
                            scheduled: up.data?.scheduled_downgrade || null,
                        });
                        if (up.data?.current_subscription_id) setCurrentSubId(up.data.current_subscription_id);
                    }
                }
            } catch (e) {
                if (alive) setLoadError(e?.message || e?.error || t('Failed to load plans'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [productType, resourceId]);

    const paid = useMemo(() => products.filter((p) => Object.values(priceMapOf(p)).some((x) => x.price > 0)), [products]);

    const cycles = useMemo(() => {
        if (!paid.length) return CYCLES.filter((c) => c.value === 'monthly');
        const avail = CYCLES.filter((c) => paid.every((p) => priceMapOf(p)[c.value] !== undefined));
        return avail.length ? avail : CYCLES.filter((c) => c.value === 'monthly');
    }, [paid]);

    useEffect(() => {
        if (!cycles.find((c) => c.value === cycle)) setCycle(cycles[0]?.value || 'monthly');
    }, [cycles, cycle]);

    const discountOf = (product, cyc) => {
        const m = priceMapOf(product);
        if (cyc === 'monthly' || !m.monthly?.price || !m[cyc]?.price) return 0;
        const months = CYCLES.find((c) => c.value === cyc)?.months || 1;
        return Math.max(0, Math.round((1 - m[cyc].price / (m.monthly.price * months)) * 100));
    };
    const maxDiscount = (cyc) => Math.max(0, ...paid.map((p) => discountOf(p, cyc)));

    const priceOf = (product) => {
        const m = priceMapOf(product);
        const free = Object.values(m).length > 0 && Object.values(m).every((x) => !x.price);
        if (free) return { free: true };
        const entry = m[cycle] || m.monthly || Object.values(m)[0];
        const cyc = m[cycle] ? cycle : (m.monthly ? 'monthly' : Object.keys(m)[0]);
        if (!entry) return null;
        return { ...entry, cycle: cyc };
    };

    const priceText = (product) => {
        const p = priceOf(product);
        if (!p) return t('Price TBD');
        if (p.free) return t('Free');
        return `${formatMoney(p.price, p.currency, { decimals: Number.isInteger(p.price) ? 0 : 2 })}/${t(CYCLE_UNIT[p.cycle] || p.cycle)}`;
    };

    const isCurrent = (product) => currentSub && (Number(currentSub.product_id) === product.id || currentSub.product_name === product.name);

    const cartAdded = () => {
        window.dispatchEvent(new CustomEvent('cart:updated'));
        navigate(userPath('/billing/cart'));
    };
    const errText = (e, fallback) => {
        if (e?.code === 'PLAN_CONFLICT' && Array.isArray(e.conflicts) && e.conflicts.length) {
            return `${e.error || e.message || fallback}：${e.conflicts.map((c) => (typeof c === 'string' ? c : (c.message || c.name || JSON.stringify(c)))).join('；')}`;
        }
        return e?.error || e?.message || fallback;
    };

    // 资源当前没有套餐：直接按资源加入购物车
    const addToCart = (product) => {
        const p = priceOf(product);
        const billingCycle = p?.free ? (Object.keys(priceMapOf(product))[0] || 'onetime') : (p?.cycle || cycle);
        Modal.confirm({
            title: t('Add {{name}} to cart', { name: product.name }),
            content: (
                <div>
                    <p>{t('Plan price:')}{priceText(product)}</p>
                    {discountOf(product, billingCycle) > 0 && <p style={{ color: '#10b981', fontSize: 12 }}>{t('{{discount}}% discount applied', { discount: discountOf(product, billingCycle) })}</p>}
                </div>
            ),
            okText: t('Add to cart'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setBusy(true);
                try {
                    const r = await cartService.addItem({ product_id: product.id, billing_cycle: billingCycle, quantity: 1, resource_id: resourceId });
                    if (r.success) cartAdded(); else message.error(errText(r, t('Failed to add')));
                } catch (e) {
                    message.error(errText(e, t('Failed to add')));
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    // 升级 / 降级：加入购物车，付款后生效
    const addChange = async (productId, billingCycle) => {
        setBusy(true);
        try {
            const r = await cartService.addChange({ subscription_id: currentSubId, product_id: productId, ...(billingCycle ? { billing_cycle: billingCycle } : {}) });
            if (r.success) cartAdded(); else message.error(errText(r, t('Operation failed')));
        } catch (e) {
            message.error(errText(e, t('Operation failed')));
        } finally {
            setBusy(false);
        }
    };

    // 降级（续费时生效）：续费单按低档计价，付款后切换；先确认超限影响
    const downgrade = (product, opt) => {
        Modal.confirm({
            title: t('Downgrade to {{name}}?', { name: product.name }),
            width: 480,
            content: (
                <div>
                    <p>{t('New plan price: {{price}}/{{cycle}}', { price: formatMoney(opt.price, opt.currency), cycle: t(CYCLE_UNIT[opt.billing_cycle] || opt.billing_cycle) })}</p>
                    <p>{opt.effective_at
                        ? t('Your current plan runs until {{date}}; the new plan applies after you pay the renewal.', { date: String(opt.effective_at).slice(0, 10) })
                        : t('The new plan applies right after you pay the renewal.')}</p>
                    {(opt.warnings || []).map((w) => <p key={w} style={{ color: '#d4380d', marginBottom: 4 }}>⚠ {w}</p>)}
                </div>
            ),
            okText: t('Add to cart'),
            cancelText: t('Cancel'),
            onOk: () => addChange(opt.product_id),
        });
    };

    const upgrade = (product) => {
        const opt = upgrades.find((u) => Number(u.product_id) === product.id || u.name === product.name);
        if (!opt) {
            message.warning(t('Your current subscription cannot be changed directly to this plan'));
            return;
        }
        // 免费 → 付费：按所选周期全价，从付款当天起算；付费 → 更高档：按剩余天数折算，周期不变
        const fromFree = !!upInfo.free;
        const cyc = (fromFree ? (opt.cycles || []).find((c) => c.billing_cycle === cycle) : null) || (opt.cycles || [])[0]
            || { billing_cycle: opt.billing_cycle, price: opt.price, cost: opt.prorated_cost };
        const cur = cyc.currency || opt.currency || priceOf(product)?.currency;
        Modal.confirm({
            title: t('Confirm upgrade to {{name}}', { name: product.name }),
            content: (
                <div>
                    <p>{t('New plan price:')}<strong>{formatMoney(cyc.price, cur)}</strong>/{t(CYCLE_UNIT[cyc.billing_cycle] || cyc.billing_cycle)}</p>
                    {!fromFree && opt.days_remaining !== undefined && opt.days_remaining !== null && (
                        <p>{t('Time left in current cycle: {{remaining}} days / {{total}} days', { remaining: opt.days_remaining, total: opt.total_days || 30 })}</p>
                    )}
                    <p>{t('Due now: ')}<strong>{formatMoney(cyc.cost, cur)}</strong></p>
                    <p style={{ marginTop: 16 }}><Text type="secondary">{fromFree ? t('Takes effect after payment; billing starts on the payment date.') : t('Takes effect after payment; the expiry date stays the same.')}</Text></p>
                </div>
            ),
            okText: t('Add to cart'),
            cancelText: t('Cancel'),
            onOk: () => addChange(opt.product_id, fromFree ? cyc.billing_cycle : undefined),
        });
    };

    const renderValue = (def, raw) => {
        if (raw === undefined || raw === null || raw === '') return <Text type="secondary">-</Text>;
        if (typeof raw === 'boolean' || raw === 'true' || raw === 'false') {
            const on = raw === true || raw === 'true';
            return on ? <CheckOutlined style={{ color: '#10b981' }} /> : <CloseOutlined style={{ color: '#c3cad8' }} />;
        }
        if (Number(raw) === -1) return <Tag color="green" style={{ marginInlineEnd: 0 }}>{t('Unlimited')}</Tag>;
        if (def.format) return <Text strong>{def.format(raw)}</Text>;
        return <Text strong>{Number.isFinite(Number(raw)) ? Number(raw).toLocaleString('zh-CN') : String(raw)}{def.unit ? ` ${t(def.unit)}` : ''}</Text>;
    };

    if (!resourceId) {
        return <Navigate to={userPath(resourceListPath)} replace />;
    }

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
    }

    const midIndex = products.length >= 3 ? products.length - 2 : -1; // 次高档默认推荐

    // 「套餐」页签内容（原整页主体）
    const plansTab = (
        <>
            {currentSub ? (
                <Alert type="success" showIcon style={{ marginBottom: 16 }}
                    message={t('Current plan for this {{resource}}: {{plan}}', { resource: t(resourceLabel), plan: currentSub.product_name || '-' })} />
            ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('This {{resource}} has no plan subscription yet', { resource: t(resourceLabel) })} />
            )}

            {upInfo.scheduled && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message={t('Downgrade to {{name}} scheduled; applies when the renewal is paid', { name: upInfo.scheduled.name })}
                    action={<Space>
                        {upInfo.scheduled.invoice_id && <Button size="small" type="primary" onClick={() => navigate(userPath(`/billing/invoices/${upInfo.scheduled.invoice_id}`))}>{t('Pay now')}</Button>}
                        <Button size="small" onClick={async () => {
                            const r = await billingService.cancelDowngrade(currentSubId);
                            if (r.success) { message.success(t('Downgrade cancelled')); setUpInfo((x) => ({ ...x, scheduled: null })); } else { message.error(r.error || t('Operation failed')); }
                        }}>{t('Cancel downgrade')}</Button>
                    </Space>}
                />
            )}
            {upInfo.pending_invoice_id && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                    message={t('An upgrade is awaiting payment; the plan changes after you pay')}
                    action={<Button size="small" type="primary" onClick={() => navigate(userPath(`/billing/invoices/${upInfo.pending_invoice_id}`))}>{t('Pay now')}</Button>} />
            )}

            {cycles.length > 1 && (!currentSub || upInfo.free) && (
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <Segmented
                        options={cycles.map((c) => {
                            const d = maxDiscount(c.value);
                            return { value: c.value, label: d > 0 ? <span>{t(c.label)} <Tag color="red" style={{ marginInlineStart: 4 }}>{t('Save {{discount}}%', { discount: d })}</Tag></span> : t(c.label) };
                        })}
                        value={cycle}
                        onChange={setCycle}
                    />
                </div>
            )}

            {products.length === 0 ? (
                <Empty description={t("No plans for sale")} />
            ) : (
                <Row gutter={[16, 16]}>
                    {products.map((product, idx) => {
                        const color = TIER_COLORS[Math.min(idx, TIER_COLORS.length - 1)];
                        const current = isCurrent(product);
                        const upOpt = currentSub ? upgrades.find((u) => Number(u.product_id) === product.id) : null;
                        const downOpt = currentSub ? (upInfo.downgrades || []).find((d) => Number(d.product_id) === product.id) : null;
                        const blocked = currentSub && !current && ((!upOpt && !downOpt) || (upOpt && upInfo.expired));
                        const recommended = idx === midIndex && !current;
                        const features = effectiveFeatures(product, featureDefs);
                        return (
                            <Col xs={24} sm={12} xl={Math.max(6, Math.floor(24 / Math.min(products.length, 4)))} key={product.id}>
                                <Card
                                    style={{ height: '100%', borderColor: current || recommended ? color : undefined, borderWidth: current || recommended ? 2 : 1 }}
                                    styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%' } }}
                                >
                                    <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                        <Space>
                                            <span style={{ color, fontSize: 20 }}>{TIER_ICONS[Math.min(idx, TIER_ICONS.length - 1)]}</span>
                                            <Title level={5} style={{ margin: 0 }}>{product.name}</Title>
                                        </Space>
                                        {current ? <Tag color="blue">{t('Current plan')}</Tag> : recommended ? <Tag color="gold">{t('Recommended')}</Tag> : null}
                                    </Space>
                                    <div style={{ margin: '12px 0 4px', fontSize: 24, fontWeight: 600, color }}>{priceText(product)}</div>
                                    {discountOf(product, cycle) > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{t('Save {{discount}}% compared to monthly billing', { discount: discountOf(product, cycle) })}</Text>}
                                    {product.description && <Text type="secondary" style={{ fontSize: 13, marginTop: 4 }}>{product.description}</Text>}
                                    <Divider style={{ margin: '12px 0' }} />
                                    <List
                                        size="small"
                                        style={{ flex: 1 }}
                                        dataSource={featureDefs.filter((d) => features[d.key] !== undefined && !(d.key === 'shared_resources_limit' && Number(features[d.key]) === 1))}
                                        renderItem={(d) => (
                                            <List.Item style={{ padding: '6px 0', border: 'none' }}>
                                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                    <Text type="secondary">{t(d.label)}</Text>
                                                    {renderValue(d, features[d.key])}
                                                </Space>
                                            </List.Item>
                                        )}
                                    />
                                    <Button
                                        type={current ? 'default' : 'primary'}
                                        block
                                        style={{ marginTop: 12 }}
                                        disabled={current || busy || blocked}
                                        onClick={() => (currentSub ? (downOpt ? downgrade(product, downOpt) : upgrade(product)) : addToCart(product))}
                                    >
                                        {current ? t('Current plan') : blocked ? (upInfo.expired ? t('Renew first') : t('Unavailable')) : currentSub ? (downOpt ? t('Downgrade at renewal') : t('Upgrade to this plan')) : t('Select a plan')}
                                    </Button>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {notes && <Card style={{ marginTop: 16 }} title={t("Feature description")} size="small">{notes}</Card>}
        </>
    );

    return (
        <div>
            <PageHeader title={title} subtitle={subtitle} />

            {loadError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t("Failed to load plans")} description={loadError} />}

            <Tabs
                activeKey={tab}
                onChange={(k) => { setTab(k); const q = new URLSearchParams(location.search); q.set('tab', k); navigate({ search: q.toString() }, { replace: true }); }}
                items={[
                    { key: 'plans', label: t('Plan'), children: plansTab },
                    { key: 'addons', label: t('Add-ons'), children: <AddonsTab subscriptionId={currentSubId} free={!!upInfo.free} /> },
                ]}
            />
        </div>
    );
};

export default ProductPlans;
